import { useEffect, useState } from 'react';
import { learningApi } from '../../../api/learning.js';
import { hrApi } from '../../../api/hr.js';

/** Certifications wallet (per-employee, with expiry badges) + an expiring report. */
export default function Certifications() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [certs, setCerts] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      hrApi.listEmployees().catch(() => ({ items: [] })),
      learningApi.expiringCertifications(30).catch(() => ({ items: [] })),
    ])
      .then(([e, x]) => {
        setEmployees(e?.items || e || []);
        setExpiring(x?.items || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  async function loadWallet(empId) {
    setEmployeeId(empId);
    setCerts([]);
    if (!empId) return;
    setLoading(true);
    try {
      const res = await learningApi.listCertifications({ employee_id: empId });
      setCerts(res?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load certifications');
    } finally {
      setLoading(false);
    }
  }

  async function revoke(id) {
    try {
      await learningApi.revokeCertification(id);
      if (employeeId) loadWallet(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke');
    }
  }

  const badgeClass = (s) => (s === 'active' ? 'badge ok' : s === 'expired' ? 'badge warn' : 'badge');

  return (
    <section data-testid="learning-certifications-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Certifications</h1>
          <p className="muted">Wallet + expiry tracking</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="learning-certifications-error">{error}</div>}

      <div className="card">
        <h3>Wallet</h3>
        <label>Employee
          <select data-testid="learning-cert-employee-input" value={employeeId} onChange={(e) => loadWallet(e.target.value)}>
            <option value="">— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        <table className="data-table" data-testid="learning-cert-list">
          <thead><tr><th>Certificate</th><th>Issued</th><th>Expires</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {certs.map((c) => (
              <tr key={c.id} data-testid={`learning-cert-row-${c.id}`}>
                <td>{c.name}</td>
                <td>{c.issued_at ? String(c.issued_at).slice(0, 10) : '—'}</td>
                <td>{c.expires_at ? String(c.expires_at).slice(0, 10) : 'never'}</td>
                <td><span className={badgeClass(c.effective_status)} data-testid={`learning-cert-badge-${c.id}`}>{c.effective_status}</span></td>
                <td>{c.effective_status !== 'revoked' && <button className="btn ghost" data-testid={`learning-cert-revoke-${c.id}`} onClick={() => revoke(c.id)}>Revoke</button>}</td>
              </tr>
            ))}
            {employeeId && certs.length === 0 && !loading && <tr><td colSpan={5} className="muted">No certifications.</td></tr>}
            {!employeeId && <tr><td colSpan={5} className="muted">Select an employee.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Expiring within 30 days</h3>
        <table className="data-table" data-testid="learning-cert-expiring-list">
          <thead><tr><th>Certificate</th><th>Employee</th><th>Expires</th></tr></thead>
          <tbody>
            {expiring.map((c) => (
              <tr key={c.id} data-testid={`learning-cert-expiring-row-${c.id}`}>
                <td>{c.name}</td><td>{c.employee_name || '—'}</td><td>{c.expires_at ? String(c.expires_at).slice(0, 10) : '—'}</td>
              </tr>
            ))}
            {expiring.length === 0 && !loading && <tr><td colSpan={3} className="muted">Nothing expiring soon.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
